/**
 * 지도 feature 클릭 분류 — if/else 대신 Policy(Strategy) 테이블로 분기
 */
import { MODULE_HIERARCHY_ROW_KIND } from './moduleHierarchyKeys';
import { resolveHierarchyPks } from './moduleHierarchySelection';

export const MAP_FEATURE_CLICK_KIND = {
  IGNORE: 'ignore',
  DEFECT: 'defect',
  MODULE: 'module',
  RGB: 'rgb',
  OTHER: 'other'
};

/**
 * 결함 마커 드래그/클릭 대상 여부.
 * WFS: "module_defects.fid-…" | "ai_detect_defects.fid-…" (둘 다 module_defect.seq)
 */
export function isDraggableDefectFeatureId(rawId) {
  if (rawId == null || rawId === '') {
    return false;
  }
  const dotToken = String(rawId).split('.')[0];
  return (
    dotToken.startsWith('module_defects') || dotToken.startsWith('ai_detect_defects')
  );
}

/**
 * 픽셀 히트 목록(위→아래)에서 첫 결함 마커.
 * 확정(module_defects*)·AI(ai_detect_defects*) 동일. 하이라이트·모듈은 건너뛴다.
 */
export function findDraggableDefectFeature(featuresTopFirst) {
  if (!Array.isArray(featuresTopFirst)) {
    return null;
  }
  for (let i = 0; i < featuresTopFirst.length; i += 1) {
    const feature = featuresTopFirst[i];
    if (isDraggableDefectFeatureId(feature?.getId?.())) {
      return feature;
    }
  }
  return null;
}

/** 드래그 히트에서 제외할 레이어 (포커스 점·표시 전용) */
export function isDefectDragHitLayer(layer) {
  if (!layer || typeof layer.get !== 'function') {
    return true;
  }
  if (layer.get('displayOnly') === true) return false;
  if (layer.get('isHighlightLayer') === true) return false;
  if (layer.get('interactive') === false) return false;
  return true;
}

/** WFS: "module_defects.fid-…" / siteDesign 폴리곤: "solar_module_4305" */
export function classifyMapFeatureClick(feature) {
  const rawId = feature?.getId?.();
  if (rawId == null || rawId === '') {
    return MAP_FEATURE_CLICK_KIND.IGNORE;
  }

  const id = String(rawId);
  const dotToken = id.split('.')[0];

  if (isDraggableDefectFeatureId(rawId)) {
    return MAP_FEATURE_CLICK_KIND.DEFECT;
  }
  if (dotToken === 'solar_module' || id.startsWith('solar_module')) {
    return MAP_FEATURE_CLICK_KIND.MODULE;
  }
  if (dotToken === 'solar_rgb_image' || id.startsWith('solar_rgb_image')) {
    return MAP_FEATURE_CLICK_KIND.RGB;
  }

  return MAP_FEATURE_CLICK_KIND.OTHER;
}

/**
 * 모듈리스트 지도 클릭 — feature 히트 대신 좌표→siteDesign 폴리곤 폴백
 * IR 선택 사각(thermalImgFeature)은 displayOnly — hitTestLayerFilter 로 항상 픽셀 히트 제외,
 * RGB 등 OTHER feature 는 폴리곤 아래 모듈 선택을 위해 coordinate 폴백한다.
 */
export function shouldUseCoordinateModuleClickFallback(feature) {
  if (!feature) {
    return true;
  }
  const kind = classifyMapFeatureClick(feature);
  if (kind === MAP_FEATURE_CLICK_KIND.DEFECT) {
    return false;
  }
  if (kind === MAP_FEATURE_CLICK_KIND.MODULE) {
    return false;
  }
  return true;
}

/**
 * 지도 선택 오버레이(책갈피) 표시 여부
 * ol-popup 과 분리 — Module 포함 모든 tier 에 책갈피 표시
 * (Module 클릭 시 팝업만 mapFeatureClickPolicy + dismissMapFeaturePopup 으로 차단)
 */
export function shouldShowMapSelectionOverlay(target) {
  const tier = resolveHierarchyPks(target)?.tier;
  return tier === 'array' || tier === 'string' || tier === 'module';
}

/** 모듈리스트 트리 클릭 시 카메라 이동 — Array만 */
export function shouldFitMapToHierarchyTarget(target) {
  return target?.kind === MODULE_HIERARCHY_ROW_KIND.ARRAY;
}

/** Array는 검수/View 공통으로 지도 하이라이트. String은 View 모드만. */
export function shouldEmitMapHighlightOnTreeToggle(row, isViewMode) {
  if (!row?.kind) {
    return false;
  }
  if (row.kind === MODULE_HIERARCHY_ROW_KIND.ARRAY) {
    return true;
  }
  return Boolean(isViewMode);
}
